import { MediaThumbnail } from "@wags/ui/media-thumbnail.tsx";
import { cn } from "@wags/ui/ui-utils";
import { isVideoMedia } from "@wags/catalog/pure.ts";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { useSnapScroll } from "@wags/tanstack-start/browser";
import { SNAPPY_SPRING } from "@wags/ui/motion.ts";

export function MobilePhotoCarousel({
    mediaItems,
    alt,
    onImageClick,
    indicator = "counter",
    confined = false,
    className,
    ...props
}: {
    mediaItems: Array<{ id: string; url: string; mimeType?: string | null }>;
    alt: string;
    onImageClick?: (index: number) => void;

    indicator?: "counter" | "dots";

    confined?: boolean;
} & Omit<React.ComponentProps<"div">, "children">) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const { activeIndex } = useSnapScroll(scrollRef);

    if (mediaItems.length === 0) return null;

    const useDots = indicator === "dots";

    return (
        <section
            aria-label={`${alt} gallery`}
            aria-roledescription="carousel"
            data-slot="mobile-photo-carousel"
            className={cn("relative md:hidden", className)}
            {...props}
        >
            <div
                ref={scrollRef}
                className={cn(
                    "flex overflow-x-auto snap-x snap-mandatory scrollbar-none",

                    confined ? "rounded-card" : "gap-3 px-6",
                )}
            >
                {mediaItems.map((item, index) => (
                    <CarouselSlide
                        key={item.id}
                        item={item}
                        index={index}
                        alt={alt}
                        priority={index === 0}
                        onImageClick={onImageClick}
                        confined={confined}
                    />
                ))}
            </div>
            {mediaItems.length > 1 &&
                (useDots ? (
                    <CarouselDots
                        mediaItems={mediaItems}
                        activeIndex={activeIndex}
                    />
                ) : (
                    <div
                        aria-live="polite"
                        className="absolute right-9 bottom-3 rounded-label glass-pill-light px-2.5 py-1 text-body-small text-doberman-black tabular-nums"
                    >
                        <span aria-hidden="true">
                            {activeIndex + 1}/{mediaItems.length}
                        </span>
                        <span className="sr-only">
                            Media {activeIndex + 1} of {mediaItems.length}
                        </span>
                    </div>
                ))}
        </section>
    );
}

const DOT_WINDOW = 7;

function CarouselDots({
    mediaItems,
    activeIndex,
}: {
    mediaItems: Array<{ id: string }>;
    activeIndex: number;
}) {
    const reduce = useReducedMotion();
    const count = mediaItems.length;
    const half = Math.floor(DOT_WINDOW / 2);
    const windowed = count > DOT_WINDOW;

    const center = windowed
        ? Math.min(Math.max(activeIndex, half), count - 1 - half)
        : 0;
    const start = windowed ? center - half : 0;
    const end = windowed ? center + half : count - 1;
    const hasMoreLeft = start > 0;
    const hasMoreRight = end < count - 1;

    const dots = [];
    for (let i = start; i <= end; i++) {
        const distStart = i - start;
        const distEnd = end - i;
        const scale =
            (hasMoreLeft && distStart === 0) || (hasMoreRight && distEnd === 0)
                ? 0.5
                : (hasMoreLeft && distStart === 1) ||
                    (hasMoreRight && distEnd === 1)
                  ? 0.75
                  : 1;
        dots.push({ id: mediaItems[i].id, active: i === activeIndex, scale });
    }

    return (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
            <div className="flex items-center gap-1.5 drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.45)]">
                <AnimatePresence initial={false} mode="popLayout">
                    {dots.map((dot) => (
                        <motion.span
                            key={dot.id}
                            layout={!reduce}
                            initial={reduce ? false : { scale: 0, opacity: 0 }}
                            animate={{ scale: dot.scale, opacity: 1 }}
                            exit={
                                reduce
                                    ? { opacity: 0 }
                                    : { scale: 0, opacity: 0 }
                            }
                            transition={
                                reduce ? { duration: 0.12 } : SNAPPY_SPRING
                            }
                            className={cn(
                                "size-1.5 rounded-full transition-colors duration-200",

                                dot.active
                                    ? "bg-true-white"
                                    : "bg-true-white/50",
                            )}
                        />
                    ))}
                </AnimatePresence>
            </div>
            <span aria-live="polite" className="sr-only">
                Media {activeIndex + 1} of {count}
            </span>
        </div>
    );
}

function CarouselSlide({
    item,
    index,
    alt,
    priority,
    onImageClick,
    confined,
}: {
    item: { id: string; url: string; mimeType?: string | null };
    index: number;
    alt: string;
    priority: boolean;
    onImageClick?: (index: number) => void;
    confined?: boolean;
}) {
    const image = (
        <MediaThumbnail
            media={item}
            alt={`${alt} media ${index + 1}`}

            width={confined ? 1200 : 430}
            height={confined ? 900 : 323}
            priority={priority}
            placeholder={priority ? { width: 343, height: 257 } : undefined}
            className={cn(
                "aspect-4/3 w-full overflow-hidden",

                !confined && "rounded-card",
            )}
        />
    );

    const slideClass = "shrink-0 basis-full snap-center";

    if (onImageClick) {
        const mediaLabel = isVideoMedia(item) ? "video" : "photo";

        return (
            <button
                type="button"
                aria-label={`View ${alt} ${mediaLabel} ${index + 1} fullscreen`}
                onClick={() => onImageClick(index)}
                style={{ scrollSnapStop: "always" }}
                className={slideClass}
            >
                {image}
            </button>
        );
    }

    return (
        <div style={{ scrollSnapStop: "always" }} className={slideClass}>
            {image}
        </div>
    );
}
